import React, { useCallback } from 'react';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { OrganizationSecretItems } from 'libs/end-to-end/src/types/secrets/organization-secret-items';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { useMemo } from 'react';
import { useState } from 'react';
import { OrganizationSecretSelectionModal } from 'apps/sigma2-ui/src/secrets/components/OrganizationSecretSelectionModal';
import { ReadPatternHook } from 'apps/sigma2-ui/src/lib/hooks/useReadPattern';

export type DataSetSecretSetStatus = 'not-set' | 'set' | 'no-value';

export interface DataSetSecretSelectorHook {
  setStatus: DataSetSecretSetStatus;
  buttonBehavior: TouchTargetBehavior;
  buttonLabel: string;
  modal: React.ReactNode;
  currentSecretDescription: string;
  clearButtonBehavior: TouchTargetBehavior;
  clearButtonLabel: string;
  clearButtonDisabled: boolean;
}

export const useDataSetSecretSelector = ({
  secretReference,
  setSecretReference,
  organizationId,
  listSecretsPattern,
}: {
  secretReference: SecretReference | null;
  setSecretReference: (secretReference: SecretReference | null) => void;
  organizationId: string;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
}): DataSetSecretSelectorHook => {
  const [showModal, setShowModal] = useState(false);

  const onClose = useCallback(() => {
    setShowModal(false);

    if (
      listSecretsPattern.status !== 'done' &&
      listSecretsPattern.status !== 'error'
    ) {
      return;
    }

    listSecretsPattern.reloadEntity();
  }, [listSecretsPattern]);

  return useMemo(() => {
    const orgSecrets =
      listSecretsPattern.status === 'done'
        ? listSecretsPattern.entity.secrets
        : [];

    const orgSecret = !secretReference
      ? null
      : orgSecrets.find(
          (secret) => secret.secretId === secretReference.secretId,
        ) ?? null;

    return {
      setStatus: !orgSecret
        ? 'not-set'
        : orgSecret.secretValuePresent
          ? 'set'
          : 'no-value',
      buttonBehavior: {
        type: 'onClick',
        onClick: () => {
          setShowModal(true);
        },
      },
      buttonLabel: 'Select Secret',
      currentSecretDescription: !orgSecret ? '' : orgSecret.secretDescription,
      modal: showModal ? (
        <OrganizationSecretSelectionModal
          secretReference={secretReference}
          setSecretReference={setSecretReference}
          organizationId={organizationId}
          onClose={onClose}
          listSecretsPattern={listSecretsPattern}
        />
      ) : (
        <></>
      ),
      clearButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          setSecretReference(null);
        },
      },
      clearButtonLabel: 'Clear',
      clearButtonDisabled: secretReference === null,
    };
  }, [
    listSecretsPattern,
    organizationId,
    secretReference,
    setSecretReference,
    showModal,
    onClose,
  ]);
};
